import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type MouseEvent,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";
import styles from "./Confirm.module.css";

type ConfirmVariant = "danger" | "default";

type ConfirmOpts = {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  prompt?: {
    label?: string;
    placeholder?: string;
    defaultValue?: string;
    multiline?: boolean;
    maxLength?: number;
    required?: boolean;
  };
};

type Result = {
  confirmed: boolean;
  value?: string;
};

type ConfirmContextValue = {
  open: (opts: ConfirmOpts) => Promise<Result>;
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

type PendingState = ConfirmOpts & { resolve: (r: Result) => void };

export function ConfirmProvider({ children }: PropsWithChildren) {
  const [pending, setPending] = useState<PendingState | null>(null);
  const [value, setValue] = useState("");

  const open = useCallback((opts: ConfirmOpts) => {
    setValue(opts.prompt?.defaultValue ?? "");
    return new Promise<Result>((resolve) => {
      setPending({ ...opts, resolve });
    });
  }, []);

  const ctx = useMemo<ConfirmContextValue>(() => ({ open }), [open]);

  const close = (result: Result) => {
    pending?.resolve(result);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={ctx}>
      {children}
      {pending && (
        <ConfirmDialog
          opts={pending}
          value={value}
          onChangeValue={setValue}
          onCancel={() => close({ confirmed: false })}
          onConfirm={() => close({ confirmed: true, value })}
        />
      )}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({
  opts,
  value,
  onChangeValue,
  onCancel,
  onConfirm,
}: {
  opts: ConfirmOpts;
  value: string;
  onChangeValue: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const el = inputRef.current;
    if (el) el.focus();
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  const handleBackdrop = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onCancel();
  };

  const handleInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !submitDisabled) onConfirm();
  };

  const submitDisabled = opts.prompt?.required === true && !value.trim();

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onMouseDown={handleBackdrop}
    >
      <div className={styles.dialog}>
        <h2 id="confirm-title" className={styles.title}>
          {opts.title}
        </h2>
        {opts.body && <p className={styles.body}>{opts.body}</p>}
        {opts.prompt && (
          <label className={styles.promptRow}>
            {opts.prompt.label && <span>{opts.prompt.label}</span>}
            {opts.prompt.multiline ? (
              <textarea
                ref={(el) => {
                  inputRef.current = el;
                }}
                className={styles.input}
                rows={3}
                value={value}
                placeholder={opts.prompt.placeholder}
                maxLength={opts.prompt.maxLength}
                onChange={(e) => onChangeValue(e.target.value)}
              />
            ) : (
              <input
                ref={(el) => {
                  inputRef.current = el;
                }}
                type="text"
                className={styles.input}
                value={value}
                placeholder={opts.prompt.placeholder}
                maxLength={opts.prompt.maxLength}
                onChange={(e) => onChangeValue(e.target.value)}
                onKeyDown={handleInputKey}
              />
            )}
          </label>
        )}
        <div className={styles.actions}>
          <Button type="button" variant="secondary" onClick={onCancel}>
            {opts.cancelLabel ?? t("common.cancel")}
          </Button>
          <Button
            type="button"
            variant={opts.variant === "danger" ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={submitDisabled}
            autoFocus={!opts.prompt}
          >
            {opts.confirmLabel ?? t("common.ok")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    return async (opts: ConfirmOpts): Promise<Result> => ({
      confirmed: window.confirm(opts.title),
      value: undefined,
    });
  }
  return ctx.open;
}
